import { useState } from 'react';
import { ArrowRight, Plus, Send } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { toast } from '@/hooks/useToast';
import { ErrorBanner } from '../borrow/ui';
import { shortDateTime } from '../outpatient/ui';
import NewIndentDialog from './NewIndentDialog';
import NewTransferDialog from './NewTransferDialog';
import { TransferStatusBadge } from './TransferStatusBadge';
import { useEndIndent, useIndent, useIndents } from './useIndents';
import type { IndentFilter, IndentStatus, TransferStore } from './types';

const FILTERS: Array<{ value: IndentFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'issued', label: 'Sent' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];
const STATUS_LABEL: Record<IndentStatus, string> = {
  submitted: 'Waiting', partially_issued: 'Partly sent', issued: 'Sent', cancelled: 'Cancelled', closed: 'Closed',
};
const STATUS_VARIANT: Record<IndentStatus, 'info' | 'warn' | 'success' | 'neutral'> = {
  submitted: 'info', partially_issued: 'warn', issued: 'success', cancelled: 'neutral', closed: 'neutral',
};

export function IndentStatusBadge({ status }: { status: IndentStatus }) {
  return <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
}

/**
 * Indents: a store asks another for medicines; the supplying store sends stock
 * against it with ordinary transfers ("Send stock"), as often as it takes.
 * docs/HMS_V4_PHARMACY_MULTI_STORE_DESIGN.md §8.3.
 */
export default function IndentsView({ stores, storeId, receivingStoreId, onOpenTransfer }: {
  stores: TransferStore[];
  storeId?: number;
  /** The Main Store — the usual supplier of an indent. */
  receivingStoreId?: number;
  onOpenTransfer: (transferId: number) => void;
}) {
  const [filter, setFilter] = useState<IndentFilter>('open');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const list = useIndents(filter, storeId);
  const canCreate = stores.filter((s) => s.active).length >= 2;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SegmentedControl options={FILTERS} value={filter} onChange={setFilter} size="sm" className="w-full max-w-[420px]" />
        <span className="flex-1" />
        <Button onClick={() => setCreating(true)} disabled={!canCreate}><Plus className="h-4 w-4" /> New indent</Button>
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>{['Indent', 'Asked by → supplied by', 'Lines', 'Sent', 'Status', 'Raised'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {list.isLoading ? (
              <TableRow><TableCell colSpan={6} className="p-6 text-center">Loading…</TableCell></TableRow>
            ) : list.isError ? (
              <TableRow><TableCell colSpan={6} className="p-6 text-center text-danger">
                Could not load indents. <Button variant="ghost" size="sm" onClick={() => list.refetch()}>Try again</Button>
              </TableCell></TableRow>
            ) : !list.data?.length ? (
              <TableRow><TableCell colSpan={6} className="p-6 text-center text-muted-foreground">
                {filter === 'open' ? 'No open indents.' : 'No indents.'}
              </TableCell></TableRow>
            ) : list.data.map((i) => (
              <TableRow key={i.id} className="cursor-pointer hover:bg-muted" onClick={() => setOpenId(i.id)}>
                <TableCell className="font-mono text-xs font-semibold">
                  {i.indent_number} {i.priority === 'urgent' && <Badge variant="danger" className="ml-1 font-sans">Urgent</Badge>}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5">{i.requesting_store_name} <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /> {i.supplying_store_name}</span>
                </TableCell>
                <TableCell className="tabular-nums">{i.line_count}</TableCell>
                <TableCell className="tabular-nums">{i.units_issued} / {i.units_requested}</TableCell>
                <TableCell><IndentStatusBadge status={i.status} /></TableCell>
                <TableCell className="text-[12.5px] text-muted-foreground">{shortDateTime(i.submitted_at)} · {i.requested_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {creating && (
        <NewIndentDialog
          stores={stores}
          defaultRequestingId={storeId !== receivingStoreId ? storeId : undefined}
          defaultSupplyingId={receivingStoreId}
          onClose={() => setCreating(false)}
          onCreated={(ind) => { setCreating(false); toast({ title: `${ind.indent_number} sent to ${ind.supplying_store_name}`, variant: 'success' }); setOpenId(ind.id); }}
        />
      )}
      {openId != null && (
        <IndentDetailDialog indentId={openId} stores={stores} onClose={() => setOpenId(null)}
          onOpenTransfer={(id) => { setOpenId(null); onOpenTransfer(id); }} />
      )}
    </Card>
  );
}

function IndentDetailDialog({ indentId, stores, onClose, onOpenTransfer }: {
  indentId: number;
  stores: TransferStore[];
  onClose: () => void;
  onOpenTransfer: (transferId: number) => void;
}) {
  const query = useIndent(indentId);
  const end = useEndIndent();
  const [mode, setMode] = useState<'view' | 'cancel' | 'close' | 'send'>('view');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState('');
  const ind = query.data;
  const open = ind && (ind.status === 'submitted' || ind.status === 'partially_issued');

  const finish = (kind: 'cancel' | 'close') => {
    if (!ind) return;
    if (!reason.trim()) return setFormError(kind === 'cancel' ? 'Say why the indent is being cancelled.' : 'Say why the rest won’t be sent.');
    end.mutate({ id: ind.id, kind, reason: reason.trim() }, {
      onSuccess: (r) => { toast({ title: `${r.indent_number} ${kind === 'cancel' ? 'cancelled' : 'closed'}`, variant: 'success' }); setMode('view'); setReason(''); },
    });
  };

  if (mode === 'send' && ind) {
    return (
      <NewTransferDialog
        stores={stores}
        indent={ind}
        onClose={() => setMode('view')}
        onIssued={(t) => {
          toast({ title: `${t.transfer_number} sent for ${ind.indent_number}`, variant: 'success' });
          onOpenTransfer(t.id);
        }}
      />
    );
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[760px]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {ind ? ind.indent_number : 'Indent'} {ind && <IndentStatusBadge status={ind.status} />}
            {ind?.priority === 'urgent' && <Badge variant="danger">Urgent</Badge>}
          </DialogTitle>
        </DialogHeader>
        {!ind ? (
          <div className="p-4 text-[13px] text-muted-foreground">{query.isError ? 'Could not load this indent.' : 'Loading…'}</div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
              <div><span className="text-muted-foreground">Asked by</span> <b>{ind.requesting_store_name}</b></div>
              <div><span className="text-muted-foreground">Supplied by</span> <b>{ind.supplying_store_name}</b></div>
              <div><span className="text-muted-foreground">Raised</span> {shortDateTime(ind.submitted_at)} · {ind.requested_by}</div>
              {ind.ended_at && <div><span className="text-muted-foreground">{ind.status === 'cancelled' ? 'Cancelled' : 'Closed'}</span> {shortDateTime(ind.ended_at)} · {ind.ended_by} — {ind.end_reason}</div>}
              {ind.notes && <div className="sm:col-span-2"><span className="text-muted-foreground">Notes</span> {ind.notes}</div>}
            </div>
            <div className="overflow-x-auto">
              <Table className="min-w-[560px]">
                <TableHeader>
                  <TableRow>{['Medicine', 'Asked', 'Sent', 'Still needed'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow>
                </TableHeader>
                <TableBody>
                  {ind.items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell className="font-semibold">{it.item_name}</TableCell>
                      <TableCell className="tabular-nums">{it.qty_requested}</TableCell>
                      <TableCell className="tabular-nums">{it.qty_issued}</TableCell>
                      <TableCell className={`tabular-nums ${it.qty_remaining && open ? 'font-semibold' : 'text-muted-foreground'}`}>{open ? it.qty_remaining : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {ind.transfers.length > 0 && (
              <div className="text-[13px]">
                <div className="mb-1 font-semibold">Transfers sent for this indent</div>
                <div className="flex flex-wrap gap-2">
                  {ind.transfers.map((t) => (
                    <button key={t.id} type="button" onClick={() => onOpenTransfer(t.id)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[12.5px] hover:bg-muted">
                      <span className="font-mono">{t.transfer_number}</span> <TransferStatusBadge status={t.status} />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(mode === 'cancel' || mode === 'close') && (
              <FormField label={mode === 'cancel' ? 'Why cancel?' : 'Why won’t the rest be sent?'}
                hint={mode === 'close' ? 'What was already sent stays sent; the indent stops asking for the rest.' : 'Nothing has been sent against it yet.'}>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </FormField>
            )}
            <ErrorBanner message={formError || (end.error instanceof Error ? end.error.message : null)} />
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          {mode === 'view' && open && (
            <>
              {ind.status === 'submitted' && <Button variant="ghost" onClick={() => { setFormError(''); setMode('cancel'); }}>Cancel indent…</Button>}
              <Button variant="ghost" onClick={() => { setFormError(''); setMode('close'); }}>Close…</Button>
              <span className="flex-1" />
              <Button onClick={() => setMode('send')}><Send className="h-4 w-4" /> Send stock</Button>
            </>
          )}
          {(mode === 'cancel' || mode === 'close') && (
            <>
              <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
              <Button variant="danger" loading={end.isPending} onClick={() => finish(mode)}>{mode === 'cancel' ? 'Cancel indent' : 'Close indent'}</Button>
            </>
          )}
          {mode === 'view' && !open && <Button variant="ghost" onClick={onClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
